import React, { useState, useEffect } from 'react'
import { X, Check, Plus, Trash2, Tag, ChevronDown, ChevronUp, Loader2, HandCoins } from 'lucide-react'
import { Button, Input, Modal } from '../ui'
import { useQuery } from '@tanstack/react-query'
import { getPaymentMethods } from '../../services/paymentMethodService'
import { applyDiscount, removeDiscount } from '../../services/OrderService'
import { safeArray } from '../../utils/safe'

export interface PaymentDetail {
  method: string
  received: number
  amount: number
  tip?: number
}

const TIP_PRESETS = [
  { label: '5%',  value: 5  },
  { label: '10%', value: 10 },
  { label: '15%', value: 15 },
  { label: '20%', value: 20 },
]

interface PaymentModalProps {
  visible: boolean
  total: number
  onClose: () => void
  onConfirm: (payments: PaymentDetail[], tipAmount: number) => void
  loading?: boolean
  orderId?: number | string
}

function formatCOP(v: number) {
  return '$ ' + Math.round(v).toLocaleString('es-CO')
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  visible,
  total,
  onClose,
  onConfirm,
  loading,
  orderId,
}) => {
  const [payments, setPayments] = useState<PaymentDetail[]>([])
  const [received, setReceived] = useState<number | ''>(0)
  const [selectedMethod, setSelectedMethod] = useState('')

  // Discount state
  const [showDiscount, setShowDiscount] = useState(false)
  const [discountMode, setDiscountMode] = useState<'percent' | 'amount'>('percent')
  const [discountValue, setDiscountValue] = useState<number | ''>('')
  const [discountNote, setDiscountNote] = useState('')
  const [appliedDiscount, setAppliedDiscount] = useState<{
    originalTotal: number
    discountAmount: number
    discountedTotal: number
    note?: string
  } | null>(null)

  // Tip state
  const [showTip, setShowTip] = useState(false)
  const [tipPercent, setTipPercent] = useState<number | null>(null)
  const [tipCustom, setTipCustom] = useState<number | ''>('')
  const tipAmount = tipPercent !== null
    ? Math.round((appliedDiscount ? appliedDiscount.discountedTotal : total) * tipPercent / 100)
    : Number(tipCustom) || 0
  const [applyingDiscount, setApplyingDiscount] = useState(false)
  const [discountError, setDiscountError] = useState('')

  const baseTotal = appliedDiscount ? appliedDiscount.discountedTotal : total
  const effectiveTotal = baseTotal + tipAmount

  const { data: methods } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: getPaymentMethods,
    enabled: visible
  })

  const totalPaidAmount = payments.reduce((sum, p) => sum + p.amount, 0)
  const pendingAmount = Math.max(0, effectiveTotal - totalPaidAmount)
  const change = payments.reduce((sum, p) => sum + p.received, 0) - effectiveTotal

  // Reset everything when modal opens
  useEffect(() => {
    if (visible) {
      setPayments([])
      setAppliedDiscount(null)
      setShowDiscount(false)
      setShowTip(false)
      setTipPercent(null)
      setTipCustom('')
      setDiscountValue('')
      setDiscountNote('')
      setDiscountError('')
    }
  }, [visible])

  // Auto-select first method
  useEffect(() => {
    if (visible && methods && methods.length > 0) {
      const cash = methods.find(m => m.type === 'CASH')
      setSelectedMethod(cash ? cash.name : methods[0].name)
    }
  }, [visible, methods])

  // Update received when effective total or pending changes
  useEffect(() => {
    if (visible) {
      setReceived(pendingAmount > 0 ? pendingAmount : '')
    }
  }, [effectiveTotal, pendingAmount, visible])

  // ── Discount logic ────────────────────────────────────────────────────────

  const handleApplyDiscount = async () => {
    if (!orderId) {
      setDiscountError('No se puede aplicar descuento sin ID de orden.')
      return
    }
    const val = Number(discountValue)
    if (!val || val <= 0) {
      setDiscountError('Ingresa un valor de descuento válido.')
      return
    }
    if (discountMode === 'percent' && val > 100) {
      setDiscountError('El porcentaje no puede superar 100%.')
      return
    }

    setApplyingDiscount(true)
    setDiscountError('')
    try {
      const payload = discountMode === 'percent'
        ? { discountPercent: val, discountNote: discountNote || undefined }
        : { discountAmount: val, discountNote: discountNote || undefined }

      const result = await applyDiscount(orderId, payload)
      setAppliedDiscount({
        originalTotal: result.originalTotal,
        discountAmount: result.discountAmount,
        discountedTotal: result.total,
        note: result.discountNote,
      })
      setPayments([]) // reset partial payments when total changes
    } catch (err: any) {
      setDiscountError(err?.response?.data?.message ?? 'Error al aplicar el descuento.')
    } finally {
      setApplyingDiscount(false)
    }
  }

  const handleRemoveDiscount = async () => {
    if (!orderId) return
    setApplyingDiscount(true)
    try {
      await removeDiscount(orderId)
      setAppliedDiscount(null)
      setDiscountValue('')
      setDiscountNote('')
      setDiscountError('')
      setPayments([])
    } catch {
      // ignore
    } finally {
      setApplyingDiscount(false)
    }
  }

  // ── Payment logic ─────────────────────────────────────────────────────────

  const addPayment = () => {
    const recv = Number(received)
    if (recv <= 0 || !selectedMethod) return
    const appliedAmount = Math.min(recv, pendingAmount)
    setPayments(prev => [...prev, { method: selectedMethod, received: recv, amount: appliedAmount }])
    setReceived('')
  }

  const removePayment = (index: number) => {
    setPayments(prev => prev.filter((_, i) => i !== index))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const recv = Number(received)
    if (pendingAmount > 0 && recv > 0 && selectedMethod) {
      const appliedAmount = Math.min(recv, pendingAmount)
      const finalPayments = [...payments, { method: selectedMethod, received: recv, amount: appliedAmount }]
      if (finalPayments.reduce((s, p) => s + p.amount, 0) >= effectiveTotal) {
        onConfirm(finalPayments, tipAmount)
      }
      return
    }
    if (totalPaidAmount >= effectiveTotal) {
      onConfirm(payments, tipAmount)
    }
  }

  return (
    <Modal open={visible} onClose={onClose} titleId="payment-modal-title" className="max-w-md">
      <div className="flex items-start justify-between border-b border-border px-6 py-4">
        <h2 id="payment-modal-title" className="text-lg font-semibold text-text">
          Confirmar Pago
        </h2>
        <Button variant="ghost" size="sm" aria-label="Cerrar" onClick={onClose} className="h-9 w-9 rounded-full">
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">

        {/* ── Total section ── */}
        <div className="rounded-xl border border-border bg-bg p-4 space-y-1">
          {appliedDiscount && (
            <>
              <div className="flex items-center justify-between text-sm text-text-muted">
                <span>Subtotal</span>
                <span className="line-through">{formatCOP(appliedDiscount.originalTotal)}</span>
              </div>
              <div className="flex items-center justify-between text-sm text-emerald-600">
                <span>Descuento</span>
                <span>− {formatCOP(appliedDiscount.discountAmount)}</span>
              </div>
              {appliedDiscount.note && (
                <p className="text-xs text-text-muted italic">{appliedDiscount.note}</p>
              )}
            </>
          )}
          {tipAmount > 0 && (
            <div className="flex items-center justify-between text-sm text-amber-600">
              <span>Propina{tipPercent ? ` (${tipPercent}%)` : ''}</span>
              <span>+ {formatCOP(tipAmount)}</span>
            </div>
          )}
          <div className={`flex items-center justify-between text-base ${(appliedDiscount || tipAmount > 0) ? 'border-t border-border pt-2 mt-1' : ''}`}>
            <span className="font-semibold text-text">Total a Pagar</span>
            <span className={`font-bold text-xl ${appliedDiscount ? 'text-emerald-600' : 'text-text'}`}>{formatCOP(effectiveTotal)}</span>
          </div>
        </div>

        {/* ── Discount section ── */}
        {orderId && (
          <div className="rounded-xl border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => setShowDiscount(v => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-text hover:bg-muted/30 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Tag size={14} className="text-primary" />
                {appliedDiscount ? 'Descuento aplicado' : 'Agregar descuento'}
              </span>
              {showDiscount ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {showDiscount && (
              <div className="border-t border-border px-4 py-3 space-y-3 bg-bg">
                {appliedDiscount ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-text">
                        Descuento de {formatCOP(appliedDiscount.discountAmount)}
                      </p>
                      {appliedDiscount.note && (
                        <p className="text-xs text-text-muted">{appliedDiscount.note}</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveDiscount}
                      disabled={applyingDiscount}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      {applyingDiscount ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      <span className="ml-1 text-xs">Quitar</span>
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Mode toggle */}
                    <div className="flex rounded-lg border border-border overflow-hidden text-sm">
                      <button
                        type="button"
                        onClick={() => setDiscountMode('percent')}
                        className={`flex-1 py-1.5 font-medium transition-colors ${discountMode === 'percent' ? 'bg-primary text-white' : 'bg-surface text-text-muted hover:bg-muted/30'}`}
                      >
                        Porcentaje (%)
                      </button>
                      <button
                        type="button"
                        onClick={() => setDiscountMode('amount')}
                        className={`flex-1 py-1.5 font-medium transition-colors ${discountMode === 'amount' ? 'bg-primary text-white' : 'bg-surface text-text-muted hover:bg-muted/30'}`}
                      >
                        Valor fijo ($)
                      </button>
                    </div>

                    <div className="flex gap-2">
                      <Input
                        type="number"
                        inputMode="decimal"
                        placeholder={discountMode === 'percent' ? 'Ej: 10' : 'Ej: 5000'}
                        value={discountValue}
                        onChange={e => setDiscountValue(e.target.value === '' ? '' : Number(e.target.value))}
                        className="flex-1"
                        min={0}
                        max={discountMode === 'percent' ? 100 : undefined}
                      />
                      <Button
                        type="button"
                        onClick={handleApplyDiscount}
                        disabled={applyingDiscount || !discountValue}
                        className="shrink-0"
                      >
                        {applyingDiscount ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                        <span className="ml-1">Aplicar</span>
                      </Button>
                    </div>

                    <Input
                      type="text"
                      placeholder="Nota (opcional)"
                      value={discountNote}
                      onChange={e => setDiscountNote(e.target.value)}
                      maxLength={255}
                    />

                    {discountError && (
                      <p className="text-xs text-red-500">{discountError}</p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Split payments list ── */}
        {/* ── Tip section ── */}
        <div className="rounded-xl border border-border overflow-hidden">
          <button
            type="button"
            onClick={() => setShowTip(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-text hover:bg-muted/30 transition-colors"
          >
            <span className="flex items-center gap-2">
              <HandCoins size={14} className="text-amber-500" />
              {tipAmount > 0 ? `Propina: ${formatCOP(tipAmount)}` : 'Agregar propina'}
            </span>
            {showTip ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {showTip && (
            <div className="border-t border-border px-4 py-3 space-y-3 bg-bg">
              {/* Quick presets */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setTipPercent(null); setTipCustom(''); }}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold border transition ${tipPercent === null && !tipCustom ? 'bg-text text-bg border-text' : 'border-border text-text-muted hover:bg-muted/30'}`}
                >
                  Sin propina
                </button>
                {TIP_PRESETS.map(p => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => { setTipPercent(p.value); setTipCustom(''); }}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold border transition ${tipPercent === p.value ? 'bg-amber-500 text-white border-amber-500' : 'border-border text-text-muted hover:bg-muted/30'}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {/* Custom amount */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-text-muted shrink-0">Monto fijo:</label>
                <Input
                  type="number"
                  min={0}
                  placeholder="0"
                  value={tipCustom}
                  onChange={e => { setTipCustom(e.target.value === '' ? '' : Number(e.target.value)); setTipPercent(null); }}
                  className="flex-1 text-sm"
                />
              </div>
              {tipAmount > 0 && (
                <p className="text-xs text-amber-600 font-semibold">
                  Propina: {formatCOP(tipAmount)} · Total con propina: {formatCOP(effectiveTotal)}
                </p>
              )}
            </div>
          )}
        </div>

        {payments.length > 0 && (
          <div className="bg-surface-muted border border-divider rounded-md p-3 space-y-2">
            <h4 className="text-sm font-semibold text-text">Pagos divididos</h4>
            {payments.map((p, i) => (
              <div key={i} className="flex justify-between items-center text-sm">
                <span className="text-text">{p.method} <span className="text-text-muted text-xs">(Rec.: {formatCOP(p.received)})</span></span>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-text">{formatCOP(p.amount)}</span>
                  <button type="button" onClick={() => removePayment(i)} className="text-red-500 hover:bg-red-500/10 p-1 rounded transition">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
            <div className="flex justify-between items-center font-bold text-sm border-t border-divider pt-2 mt-2">
              <span className="text-text">Restante</span>
              <span className={pendingAmount > 0 ? 'text-red-500' : 'text-emerald-600'}>{formatCOP(pendingAmount)}</span>
            </div>
          </div>
        )}

        {/* ── Payment method & amount ── */}
        {pendingAmount > 0 && (
          <>
            <div>
              <label className="block text-sm font-medium text-text mb-2">Medio de Pago</label>
              <div className="grid grid-cols-2 gap-2">
                {safeArray(methods).filter(m => m.isActive).map(method => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setSelectedMethod(method.name)}
                    className={`px-3 py-2 rounded-md text-sm font-medium border transition-colors flex items-center justify-between ${selectedMethod === method.name
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'bg-white border-border text-text hover:bg-gray-50'
                    }`}
                  >
                    {method.name}
                    {selectedMethod === method.name && <Check size={14} />}
                  </button>
                ))}
                {safeArray(methods).filter(m => m.isActive).length === 0 && (
                  <p className="col-span-2 text-xs text-red-500 italic">
                    No hay métodos de pago activos. Configúralos en Ajustes.
                  </p>
                )}
                {!methods && (
                  <p className="col-span-2 text-xs text-text-muted animate-pulse">Cargando métodos...</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="payment-received" className="text-sm font-medium text-text">
                Monto Recibido
              </label>
              <div className="flex gap-2 mt-1 items-stretch">
                <Input
                  id="payment-received"
                  type="number"
                  inputMode="numeric"
                  value={received}
                  onChange={e => setReceived(e.target.value === '' ? '' : Number(e.target.value))}
                  className="text-lg flex-1"
                  autoFocus
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addPayment}
                  disabled={Number(received) <= 0 || !selectedMethod}
                  className="h-auto"
                >
                  <Plus size={18} className="mr-1" /> Agregar
                </Button>
              </div>
            </div>
          </>
        )}

        {/* ── Change ── */}
        {change > 0 && totalPaidAmount >= effectiveTotal && (
          <div className="flex items-center justify-between text-sm p-3 rounded-md bg-green-50 text-green-800">
            <span className="font-medium">Cambio / Devuelta</span>
            <span className="font-bold text-lg">{formatCOP(change)}</span>
          </div>
        )}

        <Button
          type="submit"
          disabled={loading || (pendingAmount > 0 && (Number(received) < pendingAmount || !selectedMethod))}
          isLoading={loading}
          className="w-full text-lg h-12 mt-4"
        >
          Completar Venta
        </Button>
      </form>
    </Modal>
  )
}

export default PaymentModal
